import { Link } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import LikeButton from './LikeButton';
import SaveButton from './SaveButton';

// Last-resort fallback when a post has no destination text at all to build a
// prompt from — should basically never render.
const GENERIC_FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80';

// Many posts have no approved photo yet (see TrendingDestinationCard, which
// solved this the same way) — a single static stock photo made every such
// card in a grid look like a duplicate of the last one. Generating a
// destination-specific image instead means each photo-less card at least
// looks like *that* place instead of an unrelated stock lake.
const fallbackImageFor = (destination) =>
  destination
    ? `https://image.pollinations.ai/prompt/${encodeURIComponent(`${destination} famous landmark landscape photography 8k resolution`)}?width=800&height=1000&nologo=true`
    : GENERIC_FALLBACK_IMAGE;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtFull = (d) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtDayMonth = (d) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const fmtMonthYear = (d) =>
  d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

/**
 * Collapses a start/end pair to the shortest unambiguous form, so the range
 * still fits a narrow card:
 *   same month  -> "3 – 9 Jul 2026"
 *   same year   -> "28 Jun – 3 Jul 2026"
 *   spans years -> "28 Dec 2026 – 3 Jan 2027"
 */
const formatDateRange = (start, end) => {
  if (!start) return null;
  if (!end || end < start) return fmtFull(start);

  const sameYear = start.getFullYear() === end.getFullYear();
  // A single-day trip should read "3 Jul 2026", not "3 – 3 Jul 2026".
  if (sameYear && start.getMonth() === end.getMonth() && start.getDate() === end.getDate()) {
    return fmtFull(start);
  }
  if (sameYear && start.getMonth() === end.getMonth()) {
    return `${start.getDate()} – ${end.getDate()} ${fmtMonthYear(end)}`;
  }
  if (sameYear) {
    return `${fmtDayMonth(start)} – ${fmtDayMonth(end)} ${end.getFullYear()}`;
  }
  return `${fmtFull(start)} – ${fmtFull(end)}`;
};

/**
 * "3 – 9 Jul 2026 · 7 Days / 6 Nights" — either half is dropped if the trip is
 * missing or has an unparseable date, so a partial record still renders cleanly.
 * Nights = days - 1, matching how the planner reports trip length.
 */
const buildTripMeta = (trip) => {
  const start = toDate(trip?.startDate);
  const end = toDate(trip?.endDate);

  const dateLabel = formatDateRange(start, end);

  let durationLabel = null;
  if (start && end && end >= start) {
    // Inclusive of both travel days, matching how the planner counts them.
    const days = Math.round((end - start) / MS_PER_DAY) + 1;
    const nights = days - 1;
    const dayPart = `${days} ${days === 1 ? 'Day' : 'Days'}`;
    // A same-day trip has no nights — showing "/ 0 Nights" would just be noise.
    durationLabel =
      nights > 0
        ? `${dayPart} / ${nights} ${nights === 1 ? 'Night' : 'Nights'}`
        : dayPart;
  }

  return { dateLabel, durationLabel };
};

/**
 * Tall, full-bleed itinerary card: photo, one badge, title and location.
 *
 * The engagement controls sit as siblings of the <Link>, not inside it — the
 * previous version nested <button>s within an anchor (invalid HTML, and it
 * needed preventDefault on every handler to stop the card navigating).
 */
const PostCard = ({ post }) => {
  const { slug, trip, coverPhoto } = post;

  // `review.title` was dropped from the schema in Round 12 — the review body is now a
  // single free-text `description` of up to 5000 chars, which truncates badly as a
  // heading. The destination is the reliable label.
  const title = trip?.destination || 'Untitled trip';
  // Avoid "Alleppey · Alleppey, India" when the title already is the destination.
  const locationParts = [
    trip?.destination !== title ? trip?.destination : null,
    trip?.country,
  ].filter(Boolean);
  const location = locationParts.join(', ');
  const { dateLabel, durationLabel } = buildTripMeta(trip);
  const tripStart = toDate(trip?.startDate);
  const monthLabel = tripStart
    ? tripStart.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()
    : null;

  return (
    <article className="group relative w-full">
      <Link
        to={`/community/${slug}`}
        className="relative block aspect-[4/4.6] w-full overflow-hidden rounded-[20px] bg-gray-100 shadow-sm transition-all duration-300 hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-500/40"
      >
        <img
          src={coverPhoto?.cardUrl || coverPhoto?.publicUrl || fallbackImageFor(trip?.destination)}
          alt={trip?.destination || title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />

        {/* Legibility scrim for the text at the bottom. */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />

        {(trip?.tripType || monthLabel) && (
          <div className="absolute left-4 top-4 flex items-center gap-1.5">
            {trip?.tripType && (
              <span className="rounded-md bg-white/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-800 backdrop-blur-sm">
                {trip.tripType}
              </span>
            )}
            {monthLabel && (
              <span className="rounded-md bg-white/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-800 backdrop-blur-sm">
                {monthLabel}
              </span>
            )}
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 p-5">
          <h3 className="text-lg font-bold leading-snug text-white drop-shadow-sm line-clamp-2">
            {title}
          </h3>
          {location && (
            <p className="mt-1 truncate text-sm text-white/80">{location}</p>
          )}

          {/* Wraps rather than truncates — a clipped date range is unreadable. */}
          {(dateLabel || durationLabel) && (
            <p className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs font-medium text-white/70">
              <Calendar size={12} className="shrink-0" aria-hidden="true" />
              {dateLabel && <span>{dateLabel}</span>}
              {dateLabel && durationLabel && (
                <span aria-hidden="true" className="text-white/40">·</span>
              )}
              {durationLabel && <span>{durationLabel}</span>}
            </p>
          )}
        </div>
      </Link>

      {/* Overlaid on the card but outside the anchor, so these are real buttons. */}
      <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white/90 px-1 shadow-sm backdrop-blur-sm opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-within:opacity-100">
        <LikeButton post={post} />
        <SaveButton post={post} />
      </div>
    </article>
  );
};

export default PostCard;
